import React, { useContext, useEffect, useRef, useState } from 'react';
import { isEmpty, kebabCase } from 'lodash';
import { motion } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Button from '../layouts/Button';
import Icons from '../layouts/Icons';
import BoxedContainer from '../layouts/BoxedContainer';
import LockBody from '../layouts/LockBody';
import useOutsideClick from '../../hooks/useOutsideClick';
import Link from '../layouts/Link';
import { GlobalContext } from '../../context/GlobalContext';
import Search from '../../sections/Search';
import useMediaQuery from '../../hooks/useMediaQuery';
import useScroll from '../../hooks/useScroll';

export default function DesktopHeader({ data }) {
  const { lightLogo, darkLogo, booking, mainNavigation } = data;
  const { cssClass } = useContext(GlobalContext);
  const isBrowser = typeof window !== 'undefined';
  const windowGlobal = isBrowser && window;
  const [innerContent, setInnerContent] = useState([]);
  const menuRef = useRef(null);
  const darkHeader = cssClass.includes('dark-header');
  // console.log(darkHeader);
  const [searchOpen, setSearchOpen] = useState(false);
  const scrollbarWidth = windowGlobal ? `${window.innerWidth - document.body.clientWidth}px` : '0';
  const { position, visible } = useScroll();

  const handleClick = () => setSearchOpen(!searchOpen);

  useEffect(() => {
    if (!isEmpty(innerContent)) {
      // console.log(innerContent.itemChildren);
    }
  }, [innerContent]);

  const isDesktop = useMediaQuery('(min-width: 992px)');

  useEffect(() => {
    if (!isDesktop) {
      setInnerContent([]);
    }
  }, [isDesktop]);

  useOutsideClick(menuRef, (e) => {
    const isButton = e.target.type === 'button';
    const hasLabel = mainNavigation.some(
      ({ content }) => content.subtitle === e.target.textContent,
    );
    if (!isEmpty(innerContent) && isButton && hasLabel) return;
    setInnerContent([]);
  });

  const handleInnerContent = (value) => {
    setInnerContent(value);
  };

  const logo = darkHeader && position <= 115 && isEmpty(innerContent) ? darkLogo : lightLogo;

  const handleInnerContentChildren = (value) => {
    if (isEmpty(value)) return null;

    const hasIcon = value.some(({ content }) => content.subtitle !== '');

    if (hasIcon) {
      const iconsArray = value.filter(({ content }) => content.subtitle !== '');
      const linksArray = value.filter(({ content }) => content.subtitle === '');
      return (
        <>
          <div
            className="flex flex-col flex-wrap gap-y-[12px] max-h-[280px]
                  4xl:gap-x-[120px] 2xl:gap-x-[60px] gap-x-[50px] grow">
            {iconsArray.map(
              ({ content }, index) =>
                isEmpty(content.link) || (
                  <Link
                    to={content.link.url}
                    key={index}
                    className="4xl:text-lg xl:text-base text-[15px] text-white uppercase
                        font-roboto-condensed-bold flex items-center gap-x-[19px] transition-all
                        duration-300 hover:text-red">
                    {isEmpty(content.subtitle) || (
                      <Icons
                        icon={content.subtitle}
                        className="min-w-[24px] w-[24px] h-[24px] svg-child3:fill-white
                  svg-child4:fill-white svg-child2:fill-white"
                      />
                    )}
                    <span>{content.link.name}</span>
                  </Link>
                ),
            )}
          </div>
          <div
            className="flex flex-col flex-wrap gap-y-[12px] max-h-[280px]
                  4xl:gap-x-[120px] 2xl:gap-x-[60px] gap-x-[50px]">
            {linksArray.map(({ content }, index) => (
              <Link
                to={content.link.url}
                key={index}
                className="4xl:text-lg xl:text-base text-[15px] text-white uppercase
                        font-roboto-condensed-bold flex items-center gap-x-[19px] transition-all
                        duration-300 hover:text-red">
                {isEmpty(content.subtitle) || (
                  <Icons icon={content.subtitle} className="min-w-[24px] w-[24px] h-[24px]" />
                )}
                <span>{content.link.name}</span>
              </Link>
            ))}
          </div>
        </>
      );
    }

    return (
      <div
        className="flex flex-col flex-wrap gap-y-[12px] min-w-[150px] max-h-[280px]
         4xl:gap-x-[120px] 2xl:gap-x-[60px] gap-x-[50px]">
        {value.map(({ content }, index) => (
          <Link
            to={content.link.url}
            key={index}
            className="4xl:text-lg xl:text-base text-[15px] text-white uppercase
                        font-roboto-condensed-bold flex items-center gap-x-[19px] transition-all
                        duration-300 hover:text-red">
            {isEmpty(content.subtitle) || (
              <Icons icon={content.subtitle} className="min-w-[24px] w-[24px] h-[24px] " />
            )}
            <span>{content.link.name}</span>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        width: isEmpty(innerContent) ? '100%' : `calc(100% - ${scrollbarWidth})`,
      }}>
      <div
        className={`xl:h-[200px] lg:h-[150px] h-[110px] w-full  before:content-['']
        before:absolute before:!inline before:top-0 before:left-0 before:h-5 before:w-full
        before:bg-black
        4xl:pl-[163px] 2xl:pl-[80px] pl-[40px] 4xl:pr-[159px] 2xl:pr-[80px] pr-[40px] lg:flex
        items-center justify-between 4xl:gap-x-[177px] 2xl:gap-x-[75px] xl:gap-x-[40px] gap-x-[35px]
        z-100 fixed transition-all duration-300 hidden
        ${visible ? 'translate-y-0' : '-translate-y-[100%]'}
        ${position <= 115 ? 'bg-transparent' : 'bg-bg-7'}
        ${isEmpty(innerContent) ? '' : '!bg-bg-7'}`}>
        {isEmpty(logo) || (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}>
            <Link to="/">
              <LazyLoadImage
                src={logo.url}
                className="object-contain 4xl:!w-[273px] 2xl:!w-[175px] xl:!w-[150px] !min-w-[80px]
                !min-h-[119px] !w-[150px] !h-[119px]"
              />
            </Link>
          </motion.div>
        )}
        <div className="flex 3xl:gap-x-20 lg:gap-x-10 gap-x-7 items-center">
          <div
            className="flex 4xl:gap-x-[97px] 3xl:gap-x-[90px] 2xl:gap-x-[60px] xl:gap-x-[45px]
            gap-x-[30px] items-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="flex 3xl:gap-x-[40px] xg:gap-x-[35px] xl:gap-x-[30px] gap-x-[20px]">
              {isEmpty(mainNavigation) ||
                mainNavigation.map(({ content }, index) => (
                  <div key={index}>
                    {isEmpty(content.link) ? (
                      <button
                        type="button"
                        aria-label="title-button"
                        onClick={() => handleInnerContent(content)}
                        className={`font-roboto-condensed-bold 3xl:text-[16px]
                        xl:text-[15px] text-[14px] leading-[19px] uppercase tracking-[0.6px]
                        whitespace-pre hover:text-red transition-all duration-300
                        ${
                          darkHeader && position <= 115 && isEmpty(innerContent)
                            ? 'text-black'
                            : 'text-white'
                        }
                        ${innerContent.subtitle === content.subtitle ? '!text-red' : ''}`}>
                        {content.subtitle}
                      </button>
                    ) : (
                      <Link
                        to={content.link.url}
                        className={`font-roboto-condensed-bold leading-[1.25] uppercase
                        tracking-[0.6px] whitespace-pre hover:text-red transition-all duration-300
                        ${
                          darkHeader && position <= 115 && isEmpty(innerContent)
                            ? 'text-black'
                            : 'text-white'
                        }
                        `}
                        style={{
                          fontSize: 'clamp(0.813rem, 0.323vw + 0.612rem, 1rem)',
                        }}>
                        {content.link.name}
                      </Link>
                    )}
                  </div>
                ))}
            </motion.div>
            {isEmpty(booking) || (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}>
                <Button
                  url={booking.url}
                  target={booking.target}
                  label={booking.name}
                  ariaLabel={kebabCase(booking.name)}
                  variant="primaryRed"
                  w="xl:w-[170px] w-auto lg2:w-[150px]"
                  text="text-center"
                  tagName={kebabCase(booking.name)}
                  px="px-6"
                />
              </motion.div>
            )}
          </div>
          <motion.button
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            type="button"
            aria-label="search-button"
            onClick={handleClick}
            className="flex gap-x-[16px] search-button">
            <span
              className={`font-roboto-condensed-bold leading-[1.25] uppercase tracking-[0.6px]
              ${
                darkHeader && position <= 115 && isEmpty(innerContent) ? 'text-black' : 'text-white'
              }`}
              style={{
                fontSize: 'clamp(0.813rem, 0.323vw + 0.612rem, 1rem)',
              }}>
              {data.search}
            </span>
            <Icons
              icon={data.search.toLowerCase()}
              className={`w-[19px] h-[19px] ${
                darkHeader && position <= 115 && isEmpty(innerContent)
                  ? 'stroke-black'
                  : 'stroke-white'
              }`}
            />
          </motion.button>
        </div>
      </div>

      {isEmpty(innerContent) || (
        <div
          ref={menuRef}
          className="fixed top-0 left-0 w-full h-auto bg-bg-8 xl:pt-[247px] pt-[200px]
          pb-[81px] z-5 transition-all duration-300">
          <LockBody />
          <BoxedContainer className="regular">
            <div className="flex 4xl:gap-x-[130px] 2xl:gap-x-[85px] xl:gap-x-[60px] gap-x-[45px]">
              <div className="flex flex-col 2xl:gap-y-[23px] gap-y-[18px] xl:w-[33%] w-[27%]">
                {isEmpty(innerContent.innerTitle) || (
                  <h2
                    className="text-white 2xl:text-2xl text-[38px] leading-[52px]
                  font-roboto-condensed-bold uppercase">
                    {innerContent.innerTitle}
                  </h2>
                )}
                {isEmpty(innerContent.innerDescription) || (
                  <p className="text-white 2xl:text-base text-[15px] leading-7 font-roboto-regular">
                    {innerContent.innerDescription}
                  </p>
                )}
              </div>
              <div className="flex 4xl:gap-x-[90px] gap-x-[70px] pt-[30px] grow">
                {handleInnerContentChildren(innerContent.itemChildren)}
              </div>
            </div>
          </BoxedContainer>
        </div>
      )}
      <Search isOpen={searchOpen} clickHandler={handleClick} />
    </div>
  );
}
/*
 */
